import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useJWTAuth } from '../../contexts/useJWTAuth';
import { LoadingSpinner } from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailVerified?: boolean;
}

/** Routes that should not be saved as return URLs (would cause redirect loops) */
const NO_RETURN_ROUTES = ['/', '/signin', '/signup'];

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireEmailVerified = true,
}) => {
  const { isAuthenticated, isLoading, isInitialized, user } = useJWTAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState<
    'checking' | 'authenticated' | 'unauthenticated' | 'unverified'
  >('checking');

  useEffect(() => {
    // Wait for auth initialization to complete before making any decisions
    // This prevents the "Authentication Required" modal from appearing prematurely
    if (!isInitialized || isLoading) {
      return;
    }

    // Auth is initialized - now we can make definitive decisions

    if (isAuthenticated) {
      // Check if email verification is required and user hasn't verified
      if (requireEmailVerified && user && !user.emailVerified) {
        setAuthState('unverified');
        // Only redirect if not already on the verification page
        if (!location.pathname.startsWith('/verify-email')) {
          navigate('/verify-email');
        }
        return;
      }
      setAuthState('authenticated');
      return;
    }

    // Not authenticated after initialization complete
    setAuthState('unauthenticated');
  }, [
    isAuthenticated,
    isLoading,
    isInitialized,
    navigate,
    user,
    requireEmailVerified,
    location.pathname,
  ]);

  // Show loading while auth is initializing
  if (!isInitialized || isLoading || authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (authState === 'unverified') {
    return null; // Redirect happening via useEffect
  }

  if (authState === 'unauthenticated') {
    // Save current URL so the user can return after signing in
    const currentPath = location.pathname + location.search;
    if (!NO_RETURN_ROUTES.includes(location.pathname)) {
      sessionStorage.setItem('auth_return_url', currentPath);
    }

    // auth_session_expired is set by authenticatedApi when a token refresh fails,
    // not here — first-time unauthenticated visitors should not see the banner.
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
};
